import { defineStore } from 'pinia'
import { api } from '@/plugins/api'

const baseUrl = window.config
  ? `${window.config.apiUrl}/user`
  : `${import.meta.env.VITE_API_URL}/user`

const PAGE_PERMISSIONS = {
  '/login': [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
    22, 23, 24, 25,
  ],
  '/users': [1, 2, 3, 4, 5, 7, 9, 11, 13, 15, 17],
  '/compare': [1, 2, 3, 5],
}

export const authStore = defineStore({
  id: 'auth',
  state: () => ({
    // initialize state from local storage to enable user to stay logged in
    token: JSON.parse(sessionStorage.getItem('token')),
    profile: sessionStorage.getItem('profile')
      ? JSON.parse(sessionStorage.getItem('profile'))
      : { username: null, password: null, roles: [] },
    passthrough: '',
    twoFactorEnabled: false,
  }),
  actions: {
    async login(username, password) {
      const result = await api.post(`${baseUrl}/login`, {
        username,
        password,
      })
      
      // Check if 2FA is required
      if (result?.requiresMfa) {
        return result // Return the result for MFA verification
      }
      
      // Regular login flow
      if (result?.token || result) {
        const token = result.token || result
        this.token = token
        // store the token to a session variable
        sessionStorage.setItem('token', JSON.stringify(token))
        // decode the token and establish profile
        this.decodeAndSetProfile(token, result.twoFactorEnabled)
        
        return { success: true, token }
      }
      
      return { success: false }
    },

    decodeAndSetProfile(token, twoFactorEnabled = false) {
      // decode the token
      var base64Url = token.split('.')[1]
      var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      var jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          })
          .join(''),
      )
      
      // parse the payload
      var tokenPayload = JSON.parse(jsonPayload)
      
      // establish a profile
      let profile = {
        user: parseInt(tokenPayload.uid),
        contact: parseInt(tokenPayload.cid),
        firm: parseInt(tokenPayload.fid),
        firmName: tokenPayload.bname,
        timezone: parseInt(tokenPayload.tz),
        firstName: tokenPayload.fname,
        lastName: tokenPayload.lname,
        username: tokenPayload.uname,
        email: tokenPayload.email,
        language: tokenPayload.cc,
        roles: [],
        twoFactorEnabled: false
        // Add other profile properties as needed
      }
      
      this.profile = profile
      this.profile.roles.push(parseInt(tokenPayload.rid))
      this.twoFactorEnabled = tokenPayload.twoFactorEnabled || twoFactorEnabled || false
      this.profile.twoFactorEnabled = this.twoFactorEnabled
      sessionStorage.setItem('profile', JSON.stringify(profile))
    },

    async initiate2FA() {
      try {
        const result = await api.post(`${baseUrl}/2fa/enable/initiate`)
        return result
      } catch (error) {
        console.error('Failed to initiate 2FA:', error)
        throw error
      }
    },

    async confirm2FA(data) {
      try {
        const result = await api.post(`${baseUrl}/2fa/enable/confirm`, {
          code: data.code
        })
        
        if (result?.enabled) {
          this.twoFactorEnabled = true
          // Update profile in session
          if (this.profile) {
            this.profile.twoFactorEnabled = true
            sessionStorage.setItem('profile', JSON.stringify(this.profile))
          }
        }
        
        return result
      } catch (error) {
        console.error('Failed to confirm 2FA:', error)
        throw error
      }
    },

    async verify2FA(data) {
      try {
        console.log('Verifying 2FA with data:', data)
        const result = await api.post(`${baseUrl}/2fa/verify`, {
          code: data.code,
          // Include any additional data needed for verification
          ...data.loginData
        })
        
        if (result?.token) {
          this.token = result.token
          sessionStorage.setItem('token', JSON.stringify(result.token))
          
          // Decode and set profile
          this.decodeAndSetProfile(result.token)
          
          return { success: true, token: result.token }
        }
        
        return { success: false }
      } catch (error) {
        console.error('Failed to verify 2FA:', error)
        throw error
      }
    },

    async generate2FAQRCode(otpauthUri) {
      try {
        const encodedUri = encodeURIComponent(otpauthUri)
        const response = await fetch(`${baseUrl}/2fa/qrcode?otpauth=${encodedUri}`, {
          headers: this.getAuthHeaders()
        })
        
        if (response.ok) {
          const blob = await response.blob()
          return URL.createObjectURL(blob)
        }
        
        throw new Error('Failed to generate QR code')
      } catch (error) {
        console.error('Failed to generate QR code:', error)
        throw error
      }
    },

    async disable2FA() {
      try {
        const result = await api.post(`${baseUrl}/2fa/disable`)
        
        if (result?.success) {
          this.twoFactorEnabled = false
          // Update profile in session
          if (this.profile) {
            this.profile.twoFactorEnabled = false
            sessionStorage.setItem('profile', JSON.stringify(this.profile))
          }
        }
        
        return result
      } catch (error) {
        console.error('Failed to disable 2FA:', error)
        throw error
      }
    },

    getAuthHeaders() {
      if (this.token) {
        return {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      }
      return {}
    },

    setData(data) {
      switch (data.name) {
        case 'username':
          this.profile.username = data.value
          break
        case 'password':
          this.profile.password = data.value
          break
      }
    },

    setPassthrough(passthrough) {
      this.passthrough = passthrough
    },

    clearToken() {
      this.token = null
      this.profile = null
      this.twoFactorEnabled = false
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('profile')
    },

    logout() {
      this.token = null
      this.profile = null
      this.twoFactorEnabled = false
      sessionStorage.clear()
      this.$router.push('/login')
    },

    isAllowed() {
      return (page = '') => {
        if (!PAGE_PERMISSIONS[page]) {
          return true
        }
        if (this.profile.roles && this.profile.roles.length) {
          return this.profile.roles.some(r =>
            PAGE_PERMISSIONS[page].includes(r),
          )
        }
        return PAGE_PERMISSIONS[page].includes('Guest')
      }
    },

    async resetPassword(data) {
      return await api.post('user/resetPassword', data)
    }
  },
})
